import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useForm} from "react-hook-form";
import {useIntl} from 'react-intl';
import {getLocalizedErrorMessage} from "../../helpers/backendErrorHandler";
import {loginUser, setAppLang, setSchoolYear} from "../../features/user/userSlice";
import {getMenus} from "../../features/menu/menuSlice";
import LoginForm from "./LoginForm";
import {TConnectionFormValues} from "../../constants/type/connection/connectionType";
import AuthenticationService from "../../service/AuthenticationService";
import {RoleUser} from "../../constants/type/RoleUser";
import _ from "lodash";
import {pageFormImpl, setPageForm} from "../../features/page/pageSlice";
import {clearDataCache} from "../../features/cache/remoteDBCacheSlice";
import {prefetchPage} from "../../app/pageChunkLoaders";
import {loadAppShell} from "../../app/appShellChunk";
import {ensureSchoolYearLoaded} from "../../helpers/schoolYearLoader";
import messages from "../messages/general/messages";

/** Route used when the backend returns no usable menu (e.g. empty rights). */
const DEFAULT_LANDING_PATH = '/app/dashboard';

/**
 * Menus are a tree: category nodes usually have `lien: null` and the actual
 * routes live on their descendants (e.g. "Students" -> "Children list").
 * Walk the whole tree, depth-first, and return the preferred link when it is
 * reachable, otherwise the first navigable link in server order.
 */
const findMenuLanding = (menus: any, preferred?: string | null): string | null => {
    if (!_.isArray(menus)) {
        return null;
    }

    let firstLink: string | null = null;

    const visit = (items: any[]): string | null => {
        for (const item of items) {
            if (!item) {
                continue;
            }
            const lien = item.lien;
            if (_.isString(lien) && lien.includes('/')) {
                if (preferred && lien === preferred) {
                    return lien;
                }
                if (firstLink == null) {
                    firstLink = lien;
                }
            }
            if (_.isArray(item.children)) {
                const preferredInChildren = visit(item.children);
                if (preferredInChildren) {
                    return preferredInChildren;
                }
            }
        }
        return null;
    };

    return visit(menus) ?? firstLink;
};

/**
 * Resolve the page to open after a successful login (and when an already
 * authenticated user lands on "/"). Never returns null: staying on the login
 * form with a valid session is worse than opening a default page.
 */
const resolveLandingPath = (role: any, menus: any): string => {
    const roleTarget = (role == RoleUser.ADMIN || role == RoleUser.SUPER_ADMIN || role == RoleUser.SUPPORT)
        ? '/app/dashboard'
        : (role == RoleUser.TEACHER ? '/app/child/child-day' : null);

    // Prefer the role target when the user actually has it in their menu,
    // otherwise use the first granted leaf, then the role target as a last
    // resort (menus can be missing/empty when rights were not provisioned).
    return findMenuLanding(menus, roleTarget) ?? roleTarget ?? DEFAULT_LANDING_PATH;
};

function Login(props: any) {
    const [sending, setSending] = useState(false)
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const userRef = useRef<any>(useSelector((state:any) => state.user.user));
    const form = useForm<TConnectionFormValues>({
        defaultValues: {
            username: '',
            password: '',
        }
    });
    const {register, handleSubmit, formState} = form;
    const {errors} = formState;
    const appSystemLang = window.localStorage.getItem("appSystemLang");

    const authenticationService = AuthenticationService();

    const onSubmit = async (data: TConnectionFormValues) => {
        try {
            setSending(true);
            const response = await authenticationService.authentificateUser(data);
            dispatch(setPageForm(_.cloneDeep(pageFormImpl)));
            // The gateway has already set the httpOnly auth cookie on this response;
            // nothing token-related is stored in the browser anymore.
            dispatch(loginUser(response.data));
            dispatch(getMenus(response.data));
            // Force the SchoolYearLoader (protected side) to fetch the current
            // school year, instead of blocking this login on a secondary request.
            dispatch(setSchoolYear(null));
            // Kick the year request off now: it runs in the background while the
            // app shell and landing page chunks download, so it is usually
            // already resolved by the time a page reads it.
            void ensureSchoolYearLoaded();
            window.localStorage.setItem("countLoadingPage", '1');
            // The session is healthy again: stop advertising an expired one.
            window.localStorage.removeItem('sessionExpired');

            if(appSystemLang != undefined && appSystemLang != null && appSystemLang != ''){
                dispatch(setAppLang(appSystemLang));
            }
            else {
                window.localStorage.setItem("appSystemLang", 'fr-fr');
                dispatch(setAppLang('fr-fr'));
            }

            setSending(false);
            // Land on a role-appropriate page instead of an arbitrary first menu item.
            // The menus are a tree, so the target can be nested below a category.
            const landing = resolveLandingPath(
                response.data?.user?.role,
                response.data?.user?.userDetails?.menus
            );
            // Start the shell + landing-page chunks before navigating so the
            // first authenticated screen does not wait on two round-trips.
            loadAppShell();
            prefetchPage(landing);
            navigate(landing, {replace: true});
        }
        catch (error:any){
            if(error?.code == 'ERR_NETWORK'){
                setErrorMessage(intl.formatMessage({id: messages.login_error_network}));
            }
            else {
                // Friendly localized message, translated in the language chosen by
                // the user (see backendErrorHandler module for the resolution rules).
                setErrorMessage(getLocalizedErrorMessage(error));
            }
            setSending(false);
        }
    };

    useEffect(() => {
        dispatch(clearDataCache());
        // If the session expired while using the app, the axios interceptor
        // reloads to '/' with this flag so the user sees why they were logged out.
        // The flag is deliberately NOT cleared here: this page hard-reloads once
        // right after mounting (refreshOnLogout below), which would discard a
        // one-shot message before the user could read it. It is cleared on a
        // successful login (onSubmit) and on a deliberate logout (UserMenu).
        if(window.localStorage.getItem('sessionExpired')){
            setErrorMessage(intl.formatMessage({id: messages.session_expired_message}));
        }
        const count:any = window.localStorage.getItem('countLoadingPage');
        const timer = setTimeout(() => {
            authenticationService.refreshOnLogout(count);
            if(appSystemLang != undefined && appSystemLang != null && appSystemLang != ''){
                dispatch(setAppLang(appSystemLang));
            }
            else {
                window.localStorage.setItem("appSystemLang", 'fr-fr');
                dispatch(setAppLang('fr-fr'));
            }
        }, 50);
        return () => clearTimeout(timer);

    }, []);

    useEffect(() => {
        // The persisted session is restored from sessionStorage before the app
        // renders (PersistGate), so a user with a live session who lands on "/"
        // is sent straight to their landing page.
        if(authenticationService.checkUserToken()){
            const loggedUser:any = userRef.current;
            const landing = resolveLandingPath(loggedUser?.role, loggedUser?.userDetails?.menus);
            loadAppShell();
            prefetchPage(landing);
            navigate(
                landing,
                {replace: true}
            );
        }
    }, []);

    return (
        <LoginForm
            errorMessage={errorMessage}
            sending={sending}
            register={register}
            errors={errors}
            onSubmit={handleSubmit(onSubmit)}
        />
    )
}

export default Login;
